"use client";

import { useLingui } from "@lingui/react/macro";
import Image from "next/image";
import { useEffect, useReducer } from "react";

import { prefersReducedMotion } from "./motion";
import { useSectionInView } from "./section";

/**
 * The hero's photographs (landing_hero_photos_v1 "photos" arm): small Nordic
 * businesses on an ordinary day, with the agent in its squid hat quietly doing
 * the paperwork behind them. A slow crossfade, so the headline keeps the
 * attention. Generated images of invented people: never show these as customers.
 *
 * Only the showing photo and the next one are mounted until the rotation has
 * reached the rest, so a visitor who leaves early downloads two. A photo fades
 * in only once it has loaded, so the rotation waits out a slow connection
 * instead of fading to nothing. Under reduced motion the first photo stays.
 *
 * One label on the figure rather than alt text per photo: the same sentence is
 * true of all of them, and a screen reader should not hear them change.
 */
const PHOTOS = [
  "goldsmith",
  "cafe",
  "meeting",
  "machinist",
  "florist",
  "carpenter",
  "baker",
  "hairdresser",
  "consultant",
  "realtor",
  "electrician",
] as const;

const HOLD_MS = 6000;

interface Rotation {
  readonly active: number;
  /** How many photos from the start of the list are in the DOM. */
  readonly mounted: number;
  readonly loaded: ReadonlySet<number>;
}

type RotationEvent = { readonly type: "loaded"; readonly index: number } | { readonly type: "tick" };

export const nextRotation = (state: Rotation, event: RotationEvent, total: number): Rotation => {
  if (event.type === "loaded") return { ...state, loaded: new Set(state.loaded).add(event.index) };
  const next = (state.active + 1) % total;
  if (!state.loaded.has(next)) return state;
  return { ...state, active: next, mounted: Math.min(total, Math.max(state.mounted, next + 2)) };
};

export function HeroPhotos() {
  const { t } = useLingui();
  const inView = useSectionInView();
  const [{ active, mounted }, dispatch] = useReducer(
    (state: Rotation, event: RotationEvent) => nextRotation(state, event, PHOTOS.length),
    { active: 0, mounted: 2, loaded: new Set<number>() },
  );

  useEffect(() => {
    if (!inView || prefersReducedMotion()) return;
    const timer = setInterval(() => {
      dispatch({ type: "tick" });
    }, HOLD_MS);
    return () => {
      clearInterval(timer);
    };
  }, [inView]);

  return (
    <figure
      aria-label={t`People at work in small businesses, each with a colleague in a plush squid hat quietly handling the paperwork in the background.`}
      className="relative m-0 aspect-[4/3] w-full overflow-hidden rounded-[18px] bg-surface-sunken shadow-[0_40px_80px_-40px_rgba(10,22,40,0.45)]"
    >
      {PHOTOS.slice(0, mounted).map((photo, index) => (
        <Image
          key={photo}
          src={`/landing/smb/hero/${photo}.webp`}
          alt=""
          fill
          priority={index === 0}
          sizes="(min-width: 1024px) 58vw, 100vw"
          onLoad={() => {
            dispatch({ type: "loaded", index });
          }}
          className={`object-cover transition-opacity duration-[1200ms] ease-in-out motion-reduce:transition-none ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </figure>
  );
}
